import { ThunkAction } from 'redux-thunk';
import {
    EmptyMessage,
    Message,
    ProspectConversationDetail,
    ProspectConversations,
    ProspectMessage
} from 'model/ProspectMessage';
import {
    PROSPECTMESSAGES_CLEAR_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATION_ERROR_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATION_START_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATION_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATIONS_ERROR_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATIONS_START_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATIONS_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_PATCH_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_SAVE_ERROR_ACTION_TYPE,
    PROSPECTMESSAGES_SAVE_START_ACTION_TYPE,
    PROSPECTMESSAGES_SAVE_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_SET_STORE_START_ACTION_TYPE,
    ProspectMessagesAction,
    SAVE_NEW_MESSAGE_ERROR_ACTION_TYPE,
    SAVE_NEW_MESSAGE_START_ACTION_TYPE,
    SAVE_NEW_MESSAGE_SUCCESS_ACTION_TYPE
} from './prospectMessageActionsTypes';
import store, { RootState } from 'store';
import { serialize } from 'object-to-formdata';
import { AxiosResponse } from 'axios';
import { MessageDto, ProspectChatDto, ProspectConversationsDto, ProspectMessageDto } from './prospectMessageDtos';
import ApiClient, { createMultipartTokenConfig } from 'api/ApiClient';
import {
    convertMessage,
    convertToProspectConversationDetail,
    convertToProspectConversations,
    convertToProspectMessage,
    convertToProspectMessageDto
} from './prospectMessageConverter';
import { MessageFilterTypes } from 'components/SMS/TwoWay/Model';
import { setCountSuccess } from 'actions/twoWaySms/twoWaySmsActions';

export const clear = (): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_CLEAR_ACTION_TYPE
});

export const setStoreStart = (): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_SET_STORE_START_ACTION_TYPE
});

export const fetchProspectConversationsStart = (): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_FETCH_CONVERSATIONS_START_ACTION_TYPE
});

export const fetchProspectConversationsSuccess = (
    { prospectConversations, meta }: ProspectConversations,
    filter: MessageFilterTypes | null,
    query: string | null
): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_FETCH_CONVERSATIONS_SUCCESS_ACTION_TYPE,
    payload: { prospectConversations, meta, filter, query }
});

export const fetchProspectConversationsError = (error: string): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_FETCH_CONVERSATIONS_ERROR_ACTION_TYPE,
    payload: { error }
});

export const fetchProspectConversationStart = (): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_FETCH_CONVERSATION_START_ACTION_TYPE
});

export const fetchProspectConversationSuccess = (
    prospectConversationDetail: ProspectConversationDetail,
    page: number
): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_FETCH_CONVERSATION_SUCCESS_ACTION_TYPE,
    payload: { prospectConversationDetail, page }
});

export const fetchProspectConversationError = (error: string): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_FETCH_CONVERSATION_ERROR_ACTION_TYPE,
    payload: { error }
});

export const saveProspectMessageStart = (): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_SAVE_START_ACTION_TYPE
});

export const saveProspectMessageSuccess = (message: ProspectMessage): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_SAVE_SUCCESS_ACTION_TYPE,
    payload: { message }
});

export const saveProspectMessageError = (error: string | Record<string, string[]>): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_SAVE_ERROR_ACTION_TYPE,
    payload: { error }
});

export const patchProspectMessageSuccess = (message: ProspectMessage): ProspectMessagesAction => ({
    type: PROSPECTMESSAGES_PATCH_SUCCESS_ACTION_TYPE,
    payload: { message }
});

export const saveNewMessageStart = (): ProspectMessagesAction => ({
    type: SAVE_NEW_MESSAGE_START_ACTION_TYPE
});

export const saveNewMessageSuccess = (message: Message): ProspectMessagesAction => ({
    type: SAVE_NEW_MESSAGE_SUCCESS_ACTION_TYPE,
    payload: { message }
});

export const saveNewMessageError = (error: string): ProspectMessagesAction => ({
    type: SAVE_NEW_MESSAGE_ERROR_ACTION_TYPE,
    payload: { error }
});

export const websocketMessage = (
    message: ProspectMessage,
    unreadTotal: number,
    marketplaceId?: number
): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        const prospectMessagesState = store.getState().prospectMessages;
        const paramMarketplaceId = marketplaceId ?? message.marketplaceId ?? store.getState().login.auth.marketplaceId;
        dispatch(setCountSuccess(unreadTotal));
        dispatch(
            fetchProspectConversations(
                1,
                30,
                prospectMessagesState.filter || MessageFilterTypes.NEWEST,
                prospectMessagesState.query || '',
                paramMarketplaceId
            )
        );
        const prospectConversationDetail = prospectMessagesState.prospectConversationDetail;
        if (prospectConversationDetail && prospectConversationDetail.prospect.id === message.prospectId) {
            dispatch(fetchProspectConversation(message.prospectId, 1, 30, paramMarketplaceId));
        }
    };
};

export const fetchProspectConversations = (
    page: number,
    size: number,
    order: MessageFilterTypes,
    query: string,
    marketplaceId?: number
): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        dispatch(fetchProspectConversationsStart());
        const paramMarketplaceId = marketplaceId ?? store.getState().login.auth.marketplaceId;
        const metaDataRequest = {
            page,
            size,
            order,
            phone: query
        };

        let formData = new FormData();

        const options = {
            indices: false,
            nullsAsUndefineds: false,
            booleansAsIntegers: false,
            allowEmptyArrays: true
        };

        formData = serialize(metaDataRequest, options, formData);

        try {
            const url = `/prospect_messages/conversations?marketplace_id=${paramMarketplaceId}`;
            const response: AxiosResponse<ProspectConversationsDto> = await ApiClient.post(
                url,
                formData,
                createMultipartTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(fetchProspectConversationsSuccess(convertToProspectConversations(response.data), order, query));
        } catch (error) {
            dispatch(fetchProspectConversationsError('Error retrieving prospect conversations'));
        }
    };
};

export const fetchProspectConversation = (
    prospectId: number,
    page: number,
    size: number,
    marketplaceId?: number
): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        dispatch(fetchProspectConversationStart());
        const paramMarketplaceId = marketplaceId ?? store.getState().login.auth.marketplaceId;
        const metaDataRequest = {
            page,
            size
        };

        let formData = new FormData();

        const options = {
            indices: false,
            nullsAsUndefineds: false,
            booleansAsIntegers: false,
            allowEmptyArrays: true
        };

        formData = serialize(metaDataRequest, options, formData);

        try {
            const url = `/prospect_messages/prospect/${prospectId}?marketplace_id=${paramMarketplaceId}`;
            const response: AxiosResponse<ProspectChatDto> = await ApiClient.post(
                url,
                formData,
                createMultipartTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(fetchProspectConversationSuccess(convertToProspectConversationDetail(response.data), page));
        } catch (error) {
            dispatch(fetchProspectConversationError('Error retrieving prospect conversation details'));
        }
    };
};

export const saveProspectMessage = (
    prospectMessage: ProspectMessage,
    marketplaceId?: number
): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        dispatch(saveProspectMessageStart());
        const paramMarketplaceId =
            marketplaceId ?? prospectMessage.marketplaceId ?? store.getState().login.auth.marketplaceId;
        const apiClientMethod = prospectMessage.id ? ApiClient.patch : ApiClient.post;
        const prospectMessageData = { prospect_message: convertToProspectMessageDto(prospectMessage) };

        let formData = new FormData();

        const options = {
            indices: false,
            nullsAsUndefineds: false,
            booleansAsIntegers: false,
            allowEmptyArrays: true
        };

        formData = serialize(prospectMessageData, options, formData);

        try {
            const url = prospectMessage.id
                ? `/prospect_messages/${prospectMessage.id}?marketplace_id=${paramMarketplaceId}`
                : `/prospect_messages?marketplace_id=${paramMarketplaceId}`;
            const response: AxiosResponse<ProspectMessageDto> = await apiClientMethod(
                url,
                formData,
                createMultipartTokenConfig(store.getState().login.auth.token!)
            );
            if (prospectMessage.id) {
                dispatch(patchProspectMessageSuccess(convertToProspectMessage(response.data)));
            } else {
                dispatch(saveProspectMessageSuccess(convertToProspectMessage(response.data)));
            }
        } catch (error) {
            dispatch(saveProspectMessageError('Error saving prospect message'));
        }
    };
};

export const SaveNewMessage = (
    newMessage: EmptyMessage,
    marketplaceId?: number
): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        dispatch(saveNewMessageStart());
        const paramMarketplaceId = marketplaceId ?? store.getState().login.auth.marketplaceId;
        const apiClientMethod = ApiClient.post;

        let formData = new FormData();

        const options = {
            indices: false,
            nullsAsUndefineds: false,
            booleansAsIntegers: false,
            allowEmptyArrays: true
        };

        formData = serialize(newMessage, options, formData);

        try {
            const url = `/prospect_messages/new_message?marketplace_id=${paramMarketplaceId}`;
            const response: AxiosResponse<MessageDto> = await apiClientMethod(
                url,
                formData,
                createMultipartTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(saveNewMessageSuccess(convertMessage(response.data)));
        } catch (error) {
            dispatch(saveNewMessageError('Error saving new message'));
        }
    };
};
